<template lang="pug">
  .log-weight
    .log-weight-content
      .log-weight-header Enter your weight.
      .enter-weight-input(:class="{ 'error': showError, 'valid': isValid }")
        .centered-input
          input(
            type="number"
            name="weight"
            v-model="weight"
            placeholder="0"
            step="1"
            :min="minWeight"
            :max="maxWeight"
            @input="handleInput"
          )
        .weight-unit {{ memberWeightUnitText }}
      .error-message(v-if="showError") The value you have entered is not valid. The supported range is {{ minWeight }} to {{ maxWeight }} {{ memberWeightUnitText }}.
      .save-button
        WtButton(
          @click.native="saveEntry"
          :loading="savingWeight"
          :disabled="!isValid"
        ) Next
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import { unitSettingsMixin } from "@/mixins/unitSettingsMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";

export default {
  name: "LogWeight",

  components: {
    WtTitle,
    WtButton,
    WtSvgIcon
  },

  mixins: [analyticsTrackingMixin, dateUtilityMixin, unitSettingsMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    entryItem: {
      type: Object,
      default: () => ({}),
      required: true
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true,
      validator: value => {
        return value instanceof FormData;
      }
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      weight: "",
      savingWeight: false
    };
  },

  computed: {
    ...mapGetters("members", ["member", "memberLoaded"]),

    memberWeightUnitText() {
      const memberDistanceUnits = this.memberLoaded
        ? this.memberUnits(this.member)
        : this.memberUnits(this.$auth.user());
      return memberDistanceUnits === "Kilometers" ? "kg" : "lbs";
    },

    minWeight() {
      const memberDistanceUnits = this.memberLoaded
        ? this.memberUnits(this.member)
        : this.memberUnits(this.$auth.user());

      if (memberDistanceUnits === "Kilometers") {
        return 31.75;
      } else {
        return 70;
      }
    },

    maxWeight() {
      const memberDistanceUnits = this.memberLoaded
        ? this.memberUnits(this.member)
        : this.memberUnits(this.$auth.user());

      if (memberDistanceUnits === "Kilometers") {
        return 294.8;
      } else {
        return 650;
      }
    },

    currentMetricValue() {
      // Get weight from wellnessMetrics following MakeAnEntryWeight pattern
      let metric = this._.find(this.entryItem.wellnessMetrics, {
        type: "weight"
      });
      return this._.get(metric, "value", "");
    },

    setFormData() {
      if (!this._.isEmpty(this.entryItem)) {
        return (
          this.weight !== this.currentMetricValue || this.formData.has("weight")
        );
      }
      return true;
    },

    isValid() {
      return (
        this.weight &&
        this.weight >= this.minWeight &&
        this.weight <= this.maxWeight
      );
    },

    showError() {
      return this.weight && !this.isValid;
    }
  },

  watch: {
    weight() {
      if (this.setFormData) {
        this.formData.set("weight", this.weight);
        this.$emit("formDataUpdated", true);
      }
      this.adjustInputWidth();
    },

    entryItem: {
      handler() {
        this.weight = this.currentMetricValue;
        this.adjustInputWidth();
      },
      immediate: true,
      deep: true
    },

    // Watch for uniqueKey changes to reset the form when switching dates
    uniqueKey: {
      handler() {
        this.weight = this.currentMetricValue;
        this.adjustInputWidth();
      },
      immediate: true
    }
  },

  mounted() {
    this.trackFeatureView("tracking", "tracking_weight");
    this.weight = this.currentMetricValue;
    this.adjustInputWidth();
  },

  methods: {
    ...mapActions("members", ["storeJournal", "updateJournal"]),

    adjustInputWidth() {
      this.$nextTick(() => {
        const input = this.$el.querySelector('input[name="weight"]');
        if (input) {
          const span = document.createElement("span");
          span.style.visibility = "hidden";
          span.style.position = "absolute";
          span.style.fontSize = "28px";
          span.style.fontWeight = "500";
          span.style.letterSpacing = "-1%";
          span.textContent = this.weight || "0";
          document.body.appendChild(span);

          const textWidth = span.offsetWidth;
          input.style.width = `${textWidth + 10}px`;

          document.body.removeChild(span);
        }
      });
    },

    saveEntry() {
      if (!this.isValid) return;

      // Always send the value so the backend links the post to this metric
      this.formData.set("weight", this.weight);

      this.savingWeight = true;
      this.saveEntryWrapper(this.storeJournal, this.updateJournal).finally(
        () => {
          this.savingWeight = false;
        }
      );
    },

    reset() {
      const item = "weight";
      if (this.formData.has(item)) {
        this.formData.delete(item);
      }
      this.weight = this.currentMetricValue;
      this.$nextTick(() => {
        this.$emit("formReset", true);
      });
    },

    handleInput(event) {
      this.weight = parseInt(event.target.value) || "";
    }
  }
};
</script>

<style lang="sass" scoped>
.log-weight
  height: 100%
  display: flex
  flex-direction: column
  justify-content: space-between

.log-weight-content
  flex: 1
  flex-direction: column
  display: flex
  justify-content: center
  align-items: center

  .log-weight-header
    width: 100%
    text-align: left
    font-weight: 600
    font-size: 20px
    line-height: 28px
    letter-spacing: 0px
    color: #1B1D20
    padding-bottom: 20px

  .enter-weight-input
    width: 100%
    position: relative
    border: 1px solid #E5E7EB
    border-radius: 12px
    background-color: white
    transition: all 0.2s ease
    display: flex
    align-items: center
    justify-content: center
    padding: 10px 12px

    &.valid
      border-color: #3B82F6
      background-color: #EFF6FF

    &.error
      border-color: #EF4444
      background-color: #FEF2F2

    .weight-unit
      font-size: 15px
      line-height: 23px
      letter-spacing: 0px
      font-weight: 500
      color: #9CA3AF
      pointer-events: none
      transform: translateY(20%)

    &.error .weight-unit
      color: #EF4444

    &.error .centered-input input
      color: #EF4444

  .error-message
    width: 100%
    text-align: left
    font-size: 13px
    color: #EC493F
    margin-top: 10px
    line-height: 18px
    font-weight: 500

  .save-button
    width: 100%
    padding-top: 20px

    ::v-deep .button
      width: 100% !important
      justify-content: center
      font-size: 16px
      line-height: 24px
      letter-spacing: 0px
      background-color: #2972F0
      font-weight: 600

      &:disabled
        background-color: #D1D5DB !important
        border-color: #D1D5DB !important
        color: #9CA3AF !important
        cursor: not-allowed

.centered-input
  display: flex
  align-items: center
  justify-content: center
  flex-shrink: 0

  input
    font-size: 28px
    line-height: 36px
    letter-spacing: -1%
    font-weight: 500
    color: #6B7280
    border: none
    background: transparent
    text-align: center
    padding: 0
    margin: 0
    min-width: 20px

    &:focus
      outline: none

    -webkit-appearance: none
    -moz-appearance: textfield

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button
      -webkit-appearance: none
      margin: 0
      display: none

.log-weight-form-controls
  margin-top: 20px
</style>
